import { usePage } from "@inertiajs/react"
import { useState, useRef, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Html5Qrcode } from "html5-qrcode"
import { AppLayout } from "@/components/layouts/AppLayout"
import { FlashMessages } from "@/components/FlashMessages"
import { PageHeader } from "@/components/PageHeader"
import { StageChip } from "@/components/StageChip"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { SearchableSelect } from "@/components/SearchableSelect"
import { TagMultiSelect } from "@/components/TagMultiSelect"
import { ScanLine, X, ArrowRightLeft, AlertCircle, AlertTriangle, Trash2, Camera } from "lucide-react"
import { assetTypeLabel } from "@/lib/assetTypes"
import type { AppPageProps } from "@/types"
import { isoDateInDays } from "@/lib/dates"
import type { Asset, StageWithRules, SelectOption, ProductOption } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"

interface ScanPageProps extends AppPageProps {
  stagesByType: Record<string, StageWithRules[]>
  customers: SelectOption[]
  tags: SelectOption[]
  products: ProductOption[]
}

interface MoveFormData {
  stage_id: string
  product_id: string
  customer_id: string
  batch_number: string
  expiration_date: string
  tag_ids: string[]
  notes: string
}

export default function Scan() {
  const { t } = useTranslation()
  const { auth, flash, stagesByType, customers, tags, products } = usePage<ScanPageProps>().props

  const [scanning, setScanning] = useState(false)
  const [scannedAssets, setScannedAssets] = useState<Asset[]>([])
  const [message, setMessage] = useState<{ type: "success" | "error" | "warning"; text: string } | null>(null)
  const [moveSuccess, setMoveSuccess] = useState(false)
  const [movingGroups, setMovingGroups] = useState<Set<string>>(new Set())

  const scannerRef = useRef<Html5Qrcode | null>(null)
  const scannerContainerId = "qr-scanner-container"
  const processingRef = useRef<Set<string>>(new Set())
  const [pendingStart, setPendingStart] = useState(false)

  // Per-type move form data
  const [moveFormsData, setMoveFormsData] = useState<Record<string, MoveFormData>>({})
  const [moveErrors, setMoveErrors] = useState<Record<string, Record<string, string>>>({})

  const getMoveForm = (type: string): MoveFormData => {
    return moveFormsData[type] || { stage_id: "", product_id: "", customer_id: "", batch_number: "", expiration_date: "", tag_ids: [], notes: "" }
  }

  const setMoveForm = (type: string, data: Partial<MoveFormData>) => {
    setMoveFormsData(prev => ({
      ...prev,
      [type]: { ...getMoveForm(type), ...data }
    }))
  }

  const groupedAssets = scannedAssets.reduce<Record<string, Asset[]>>((acc, asset) => {
    const type = asset.assetable_type
    if (!acc[type]) acc[type] = []
    acc[type].push(asset)
    return acc
  }, {})

  const startScanning = useCallback(() => {
    setMessage(null)
    setMoveSuccess(false)
    setScanning(true)
    setPendingStart(true)
  }, [])

  // Start the scanner after the container div is in the DOM
  useEffect(() => {
    if (!pendingStart || !scanning) return
    setPendingStart(false)

    const html5Qrcode = new Html5Qrcode(scannerContainerId)
    scannerRef.current = html5Qrcode

    html5Qrcode.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => {
        if (processingRef.current.has(decodedText)) return
        processingRef.current.add(decodedText)

        const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

        fetch("/scan/resolve", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken || "",
            "Accept": "application/json",
          },
          body: JSON.stringify({ code: decodedText }),
        })
          .then(res => {
            if (!res.ok) {
              setMessage({ type: "error", text: t("scan.assetNotFound") })
              return null
            }
            return res.json()
          })
          .then(data => {
            if (!data?.asset) return

            setScannedAssets(prev => {
              if (prev.some(a => a.id === data.asset.id)) {
                setMessage({ type: "warning", text: t("scan.assetAlreadyScanned") })
                return prev
              }
              setMessage({ type: "success", text: t("scan.scanSuccess", { type: assetTypeLabel(data.asset.assetable_type, t), code: data.asset.code }) })

              // Stop camera after successful scan
              if (scannerRef.current) {
                scannerRef.current.stop().then(() => {
                  scannerRef.current?.clear()
                  scannerRef.current = null
                }).catch(() => {})
                setScanning(false)
              }

              return [...prev, data.asset]
            })
          })
          .catch(() => {
            setMessage({ type: "error", text: t("scan.assetNotFound") })
          })
          .finally(() => {
            setTimeout(() => {
              processingRef.current.delete(decodedText)
            }, 3000)
          })
      },
      () => {}
    ).catch(() => {
      setMessage({ type: "error", text: t("scan.cameraPermission") })
      setScanning(false)
    })
  }, [pendingStart, scanning, t])

  const stopScanning = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop()
        scannerRef.current.clear()
      } catch {
        // ignore
      }
      scannerRef.current = null
    }
    setScanning(false)
  }, [])

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {})
      }
    }
  }, [])

  const removeAsset = (id: string) => {
    setScannedAssets(prev => prev.filter(a => a.id !== id))
  }

  const clearAll = () => {
    setScannedAssets([])
    setMoveFormsData({})
    setMoveErrors({})
    setMoveSuccess(false)
    setMessage(null)
  }

  const handleMoveAll = async () => {
    setMessage(null)
    const types = Object.keys(groupedAssets)
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    let allSuccess = true
    const newErrors: Record<string, Record<string, string>> = {}

    for (const type of types) {
      const form = getMoveForm(type)
      if (!form.stage_id) {
        newErrors[type] = { stage_id: t("scan.selectStage") }
        allSuccess = false
        continue
      }

      const stages = stagesByType[type] || []
      const selectedStage = stages.find(s => s.id === form.stage_id)
      const rules = selectedStage?.rules || []
      const isKeg = type === "Keg"
      const clearsProduct = isKeg && rules.includes("clear_product")
      const clearsCustomer = rules.includes("clear_customer")
      const clearsTags = rules.includes("clear_tags")
      const clearsBatch = isKeg && rules.includes("clear_batch")

      const submitData = {
        stage_id: form.stage_id,
        product_id: clearsProduct ? "" : form.product_id,
        customer_id: clearsCustomer ? "" : form.customer_id,
        batch_number: clearsBatch ? "" : form.batch_number,
        expiration_date: clearsProduct ? "" : form.expiration_date,
        tag_ids: clearsTags ? [] : form.tag_ids,
        notes: form.notes,
        asset_ids: groupedAssets[type].map(a => a.id),
        asset_type: type,
      }

      setMovingGroups(prev => new Set(prev).add(type))

      try {
        const res = await fetch("/scan/bulk_stage_move", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken || "",
            "Accept": "application/json",
          },
          body: JSON.stringify(submitData),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok || !data?.success) {
          if (data?.errors) {
            const fe: Record<string, string> = {}
            for (const [k, v] of Object.entries(data.errors)) {
              fe[k] = Array.isArray(v) ? (v as string[])[0] : (v as string)
            }
            newErrors[type] = fe
          } else {
            newErrors[type] = { _general: t("inventory.bulk_move_failed") }
          }
          allSuccess = false
        }
      } catch {
        newErrors[type] = { _general: t("inventory.bulk_move_failed") }
        allSuccess = false
      } finally {
        setMovingGroups(prev => {
          const next = new Set(prev)
          next.delete(type)
          return next
        })
      }
    }

    setMoveErrors(newErrors)

    if (allSuccess) {
      setMoveSuccess(true)
      setScannedAssets([])
      setMoveFormsData({})
    }
  }

  const messageClasses = {
    success: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200",
    error: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
    warning: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200",
  }

  const isMoving = movingGroups.size > 0

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <PageHeader
          eyebrow={t("scan.eyebrow", { defaultValue: "Leitor de códigos" })}
          title={t("scan.title")}
          subtitle={t("scan.subtitle")}
        />

        {moveSuccess && (
          <div className="p-4 bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 rounded-md">
            {t("scan.moveSuccess")}
          </div>
        )}

        {/* Scanner area */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              {!scanning ? (
                <Button size="lg" className="gap-2 text-lg px-8 py-6" onClick={startScanning}>
                  <Camera className="h-6 w-6" />
                  {scannedAssets.length > 0 ? t("scan.scanAnother") : t("scan.scanButton")}
                </Button>
              ) : (
                <>
                  <div id={scannerContainerId} className="w-full max-w-md" />
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={stopScanning} className="gap-2">
                      <X className="h-4 w-4" />
                      {t("scan.stopScanning")}
                    </Button>
                  </div>
                </>
              )}

              {message && (
                <div className={`px-4 py-2 rounded-md text-sm font-medium ${messageClasses[message.type]}`}>
                  {message.text}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Scanned assets */}
        {scannedAssets.length > 0 && (
          <>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {t("scan.scannedAssets")} ({scannedAssets.length})
              </h3>
              <Button variant="outline" size="sm" onClick={clearAll} className="gap-2">
                <Trash2 className="h-4 w-4" />
                {t("scan.clearAll")}
              </Button>
            </div>

            {Object.entries(groupedAssets).map(([type, assets]) => {
              const stages = (stagesByType[type] || []).filter(s => s.canMoveTo)
              const form = getMoveForm(type)
              const selectedStage = (stagesByType[type] || []).find(s => s.id === form.stage_id)
              const rules = selectedStage?.rules || []
              const isKeg = type === "Keg"
              const requiresProduct = isKeg && rules.includes("require_product")
              const requiresCustomer = rules.includes("require_customer")
              const requiresBatch = isKeg && rules.includes("require_batch")
              const clearsProduct = isKeg && rules.includes("clear_product")
              const clearsCustomer = rules.includes("clear_customer")
              const clearsTags = rules.includes("clear_tags")
              const clearsBatch = isKeg && rules.includes("clear_batch")
              const clearsLocation = rules.includes("clear_location")
              const errors = moveErrors[type] || {}
              const hasErrors = Object.keys(errors).length > 0

              return (
                <Card key={type}>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t("scan.assetsOfType", { type: assetTypeLabel(type as AssetType, t), count: assets.length })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Asset list */}
                    <div className="space-y-2">
                      {assets.map(asset => (
                        <div
                          key={asset.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono font-semibold tracking-wide">{asset.code}</span>
                            <StageChip stage={asset.stage} />
                            {asset.customer && (
                              <span className="text-sm text-muted-foreground">{asset.customer.name}</span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeAsset(asset.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Move stage form for this type */}
                    <div className="border-t pt-4 space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <ArrowRightLeft className="h-4 w-4" />
                        {t("scan.moveStage")}
                      </h4>

                      {hasErrors && (
                        <div className="rounded-md border border-destructive bg-destructive/10 p-3">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                            <ul className="text-sm space-y-1 text-destructive">
                              {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>{t("inventory.targetStage")} *</Label>
                        <select
                          value={form.stage_id}
                          onChange={(e) => setMoveForm(type, { stage_id: e.target.value })}
                          className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${errors.stage_id ? "border-destructive" : "border-input"}`}
                        >
                          <option value="">{t("scan.selectStage")}</option>
                          {stages.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>

                      {selectedStage && rules.length > 0 && (
                        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-yellow-800 dark:text-yellow-200">{t("inventory.rulesApplied")}</p>
                              <ul className="mt-1 space-y-1 text-yellow-700 dark:text-yellow-300">
                                {clearsProduct && <li>{t("inventory.ruleWillClearProduct")}</li>}
                                {clearsCustomer && <li>{t("inventory.ruleWillClearCustomer")}</li>}
                                {clearsBatch && <li>{t("inventory.ruleWillClearBatch")}</li>}
                                {clearsTags && <li>{t("inventory.ruleWillClearTags")}</li>}
                                {clearsLocation && <li>{t("inventory.ruleWillClearLocation")}</li>}
                                {requiresProduct && <li>{t("inventory.ruleRequiresProduct")}</li>}
                                {requiresCustomer && <li>{t("inventory.ruleRequiresCustomer")}</li>}
                                {requiresBatch && <li>{t("inventory.ruleRequiresBatch")}</li>}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {isKeg && !clearsProduct && (
                        <div className="space-y-2">
                          <Label>{t("inventory.form.productLabel")}{requiresProduct && " *"}</Label>
                          <select
                            value={form.product_id}
                            onChange={(e) => {
                              const selected = products.find((p) => p.id === e.target.value)
                              setMoveForm(type, {
                                product_id: e.target.value,
                                ...(e.target.value && selected?.default_expiration_days
                                  ? { expiration_date: isoDateInDays(selected.default_expiration_days) }
                                  : {}),
                              })
                            }}
                            className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${errors.product_id ? "border-destructive" : "border-input"}`}
                          >
                            <option value="">-</option>
                            {products.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {isKeg && !clearsProduct && (
                        <div className="space-y-2">
                          <Label>{t("inventory.form.expirationDateLabel")}</Label>
                          <Input
                            type="date"
                            value={form.expiration_date}
                            onChange={(e) => setMoveForm(type, { expiration_date: e.target.value })}
                          />
                        </div>
                      )}

                      {isKeg && requiresBatch && (
                        <div className="space-y-2">
                          <Label>{t("inventory.form.batchNumberLabel")} *</Label>
                          <Input
                            value={form.batch_number}
                            onChange={(e) => setMoveForm(type, { batch_number: e.target.value })}
                            placeholder={t("inventory.form.batchNumberPlaceholder")}
                            className={errors.batch_number ? "border-destructive" : ""}
                          />
                        </div>
                      )}

                      {!clearsCustomer && (
                        <div className="space-y-2">
                          <Label>{t("inventory.form.customerLabel")}{requiresCustomer && " *"}</Label>
                          <SearchableSelect
                            value={form.customer_id}
                            onChange={(value) => setMoveForm(type, { customer_id: value })}
                            options={customers}
                            placeholder={t("inventory.filters.all")}
                            emptyLabel="-"
                            error={!!errors.customer_id}
                          />
                        </div>
                      )}

                      {!clearsTags && (
                        <div className="space-y-2">
                          <Label>{t("inventory.form.tagsLabel")}</Label>
                          <TagMultiSelect
                            tags={tags}
                            selectedIds={form.tag_ids}
                            onChange={(ids) => setMoveForm(type, { tag_ids: ids })}
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>{t("inventory.form.notesLabel")}</Label>
                        <Textarea
                          value={form.notes}
                          onChange={(e) => setMoveForm(type, { notes: e.target.value })}
                          placeholder={t("inventory.form.notesPlaceholder")}
                          rows={3}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}

            <Button
              className="w-full gap-2"
              size="lg"
              onClick={handleMoveAll}
              disabled={isMoving || scannedAssets.length === 0}
            >
              <ArrowRightLeft className="h-4 w-4" />
              {t("scan.moveAll")} ({scannedAssets.length})
            </Button>
          </>
        )}

        {scannedAssets.length === 0 && !scanning && !moveSuccess && (
          <div className="text-center text-muted-foreground py-12">
            <ScanLine className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t("scan.noAssetsScanned")}</p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
